<script setup>
import {computed} from 'vue'
import { Menu, MenuButton } from "@headlessui/vue";
import { Disclosure } from '@headlessui/vue'

import ApplicationLogo from "@/Components/ApplicationLogo.vue";
import ProfileButton from "@/Components/ProfileButton.vue";
import { useUserStore } from "@/Stores/userStore";
import { useNavigationStore } from "@/Stores/navigationStore";
import { useRoute, useRouter } from "vue-router";
import NavigationMenuButton from "@/Components/NavigationMenuButton.vue";
import PrimaryLeftNavigationPanel from "@/Components/Navigation/PrimaryLeftNavigationPanel.vue";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import PermissionConstants from "@/Constants/permission-constants";

const userStore = useUserStore();
const navigationStore = useNavigationStore();
const router = useRouter();
const route = useRoute();

const props = defineProps({
    hideNavigation: {
        type: Boolean,
        default: false,
    },
});

const isAdminOrModerator = computed(() => {
    return userStore.hasPermission(PermissionConstants.permissions.admin) ||
           userStore.hasPermission(PermissionConstants.permissions.moderator);
});

const mobileNavigation = computed(() => {
    return [
        { name: 'Compendium', route: '/compendium', matchableRoutes: [], current: true, icon: ['fad', 'books'] },
        { name: 'Creatures', route: '/creature', matchableRoutes: ['creature', 'creature-history'], current: true, icon: ['far', 'paw-claws'] },
        { name: 'Characters', route: '/character', matchableRoutes: ['character', 'character-history'], current: true, icon: ['fad', 'users'] },
        { name: 'Equipment', route: '/equipment', matchableRoutes: ['equipment-stash', 'equipment-history'], current: true, icon: ['far', 'sword'] },
        { name: 'Maps', route: '/etch/editor', matchableRoutes: ['etch-editor', 'etch-projects', 'etch-print'], current: true, icon: ['far', 'map'] },
        { name: 'Ambience', route: '/ambience', matchableRoutes: [], current: true, icon: ['fad', 'mandolin'] },
        { name: 'Plans', route: '/pricing', matchableRoutes: [], current: true, icon: ['fad', 'coin-vertical'] },
    ];
})

const desktopNavigation = computed(() => {
    return [
        { name: 'Compendium', route: '/compendium', matchableRoutes: [], current: true, icon: ['fad', 'books'] },
        { name: 'Creatures', route: '/creature', matchableRoutes: ['creature', 'creature-history'], current: true, icon: ['far', 'paw-claws'] },
        { name: 'Characters', route: '/character', matchableRoutes: ['character', 'character-history'], current: true, icon: ['fad', 'users'] },
        { name: 'Equipment', route: '/equipment', matchableRoutes: ['equipment-stash', 'equipment-history'], current: true, icon: ['far', 'sword'] },
        { name: 'Maps', route: '/etch/editor', matchableRoutes: ['etch-editor', 'etch-projects', 'etch-print'], current: true, icon: ['far', 'map'] },
        { name: 'Ambience', route: '/ambience', matchableRoutes: [], current: true, icon: ['fad', 'mandolin'] },
        { name: 'Plans', route: '/pricing', matchableRoutes: [], current: true, icon: ['fad', 'coin-vertical'] },
    ];
})

function routeTo(page) {
    router.push({ name: page })
}

function itemRouteMatchesPath(item) {
    if (item.matchableRoutes.length > 0) {
        if (item.matchableRoutes.includes(route.name)) {
            return true
        }
    }
    return item.route === route.path
}

const isLoggedIn = computed(() => {
    return userStore.userId != null;
});


</script>

<template>
    <Disclosure as="header" class="bg-gray-800" v-slot="{ open }">
        <div class="px-2 sm:px-4 md:divide-y md:divide-gray-700 md:px-8">
            <div class="relative flex sm:h-16 h-12 justify-between">
                <div class="relative z-10 flex px-2 md:px-0">
                    <div class="flex flex-shrink-0 items-center">
                        <router-link to="/" class="clickable-container">
                            <span class="sr-only">Tavern of Azoth</span>
                            <ApplicationLogo class="fill-current text-gray-500"/>
                        </router-link>
                    </div>
                </div>
                <div class="flex">
                    <div class="relative z-50 ml-0 flex items-center">
                        <Menu as="div" class="relative mr-0 mt-2 flex-shrink-0">
                            <ProfileButton/>
                        </Menu>
                    </div>
                    <div v-if="!isLoggedIn" class="flex relative z-10 ml-0  items-center">
                        <Menu as="div" class="relative mr-2 mt-2 flex-shrink-0">
                            <NavigationMenuButton/>
                        </Menu>
                    </div>
                </div>
            </div>
            <nav class="hidden sm:flex sm:space-x-1 md:space-x-2 lg:space-x-4 xl:space-x-6 md:py-2" aria-label="Global">
                <button
                    type="button"
                    @click="navigationStore.toggleLeftPanel()"
                    class="mt-1 inline-flex items-center rounded-md py-2 px-1.5 sm:px-2 md:px-2.5 lg:px-3 text-gray-300 hover:bg-gray-700 hover:text-white text-[10px] sm:text-xs md:text-sm lg:text-base"
                >
                    <FontAwesomeIcon :icon="['far', 'bars']" size="xl"/>
                </button>
                <router-link
                    v-for="item in desktopNavigation"
                    :key="item.name"
                    :to="item.route"
                    :class="[
                        itemRouteMatchesPath(item) ? 'bg-gray-900 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white',
                        'mt-1 inline-flex items-center rounded-md py-2 font-medium',
                        'px-1.5 sm:px-2 md:px-2.5 lg:px-3 xl:px-4',
                        'text-[10px] sm:text-xs md:text-sm lg:text-base'
                    ]"
                    :aria-current="item.current ? 'page' : undefined"
                >
                    <FontAwesomeIcon :icon="item.icon" class="mr-0.5 sm:mr-1 md:mr-1.5" size="sm"/>
                    <span v-html="item.name"></span>
                </router-link>
            </nav>

            <div v-if="!hideNavigation" class="sm:hidden relative overflow-hidden">
                <nav class="py-2 flex items-center w-full overflow-x-auto scrollbar-none flex-nowrap" aria-label="Global">
                    <button
                        type="button"
                        @click="navigationStore.toggleLeftPanel()"
                        class="mt-1 inline-flex items-center justify-center rounded-md py-1 px-2 text-gray-300 hover:bg-gray-700 hover:text-white flex-shrink-0"
                    >
                        <FontAwesomeIcon :icon="['far', 'bars']" size="sm"/>
                    </button>
                    <router-link
                        v-for="item in mobileNavigation"
                        :key="item.name"
                        :to="item.route"
                        class="text-xs py-1 px-2 whitespace-nowrap flex-shrink-0"
                        :class="[itemRouteMatchesPath(item) ? 'bg-gray-900 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white', 'mt-1 inline-flex items-center rounded-md font-medium']"
                        :aria-current="item.current ? 'page' : undefined"
                    >
                        <FontAwesomeIcon :icon="item.icon" class="mr-1" size="sm"/>
                        <span v-html="item.name"></span>
                    </router-link>
                    <!-- Spacer to prevent fade from overlapping last item -->
                    <div class="w-6 flex-shrink-0"></div>
                </nav>
                <div class="mobile-nav-fade"></div>
            </div>
        </div>
    </Disclosure>
    <PrimaryLeftNavigationPanel />
</template>

<style>
.clickable-container {
    display: inline-block;
}

.scrollbar-none {
    -ms-overflow-style: none;
    scrollbar-width: none;
}

.mobile-nav-fade {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 2.5rem;
    background: linear-gradient(to right, transparent, #1f2937);
    pointer-events: none;
}

.scrollbar-none::-webkit-scrollbar {
    display: none;
}
</style>
